import * as XLSX from 'xlsx-js-style';

export function exportToExcel(data: any[], filename: string, sheetName: string = 'Rapor') {
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  const colWidths = Object.keys(data[0] || {}).map(key => {
    const maxLen = Math.max(
      key.length,
      ...data.map(row => String(row[key] ?? '').length)
    );
    return { wch: Math.min(maxLen + 2, 40) };
  });
  ws['!cols'] = colWidths;

  XLSX.writeFile(wb, `${filename}.xlsx`);
}

export function exportTableToExcel(tableId: string, filename: string) {
  const table = document.getElementById(tableId);
  if (!table) return;

  const wb = XLSX.utils.table_to_book(table, { sheet: 'Rapor' });
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

export function exportToCSV(data: any[], filename: string) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row =>
      headers.map(h => {
        const val = String(row[h] ?? '');
        return val.includes(',') || val.includes('"') || val.includes('\n')
          ? `"${val.replace(/"/g, '""')}"`
          : val;
      }).join(',')
    ),
  ].join('\n');

  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}

export function printReport(title: string) {
  window.print();
}

export function prepareExportData(reportType: string, rawData: any) {
  const exporters: Record<string, () => any[]> = {
    operation: () => {
      const { summary, dailyTrend, stageDistribution, loadTypeDistribution } = rawData;
      return [
        { Metrik: 'Toplam Sevkiyat', Deger: summary.total_shipments },
        { Metrik: 'Teslim Edilen', Deger: summary.delivered },
        { Metrik: 'Yolda', Deger: summary.in_transit },
        { Metrik: 'Atanmış', Deger: summary.assigned },
        { Metrik: 'Bekleyen', Deger: summary.open },
        { Metrik: 'İptal', Deger: summary.cancelled },
        { Metrik: 'Toplam Gelir (TL)', Deger: summary.total_revenue },
        { Metrik: 'Ortalama Mesafe (km)', Deger: Math.round(summary.avg_distance) },
        { Metrik: 'Aktif Sürücü', Deger: summary.active_drivers },
        { Metrik: 'Aktif Araç', Deger: summary.active_vehicles },
        ...dailyTrend.map((d: any) => ({ Tarih: d.date, Sevkiyat: d.count, Teslim: d.delivered, Gelir: d.revenue })),
        ...stageDistribution.map((s: any) => ({ Asama: s.stage_name, Adet: s.count })),
        ...loadTypeDistribution.map((l: any) => ({ YukTur: l.load_type, Adet: l.count })),
      ];
    },
    financial: () => {
      const { financials, expenses, monthlyRevenue, carrierTypeBreakdown } = rawData;
      return [
        { Metrik: 'Teslimat Sayısı', Deger: financials.delivered_count },
        { Metrik: 'Toplam Navlun', Deger: financials.total_navlun },
        { Metrik: 'Toplam Komisyon', Deger: financials.total_komisyon },
        { Metrik: 'Ortalama TL/KM', Deger: Math.round(financials.avg_tl_km) },
        { Metrik: 'Özmal Gelir', Deger: financials.ozmal_gelir },
        { Metrik: 'Spot Gelir', Deger: financials.spot_gelir },
        { Metrik: 'Teklif Geliri', Deger: financials.bid_gelir },
        ...monthlyRevenue.map((m: any) => ({ Ay: m.month, Sevkiyat: m.shipment_count, Gelir: m.revenue, KM: m.total_km })),
        ...carrierTypeBreakdown.map((c: any) => ({ TasarTipi: c.carrier_type, Adet: c.count, Toplam: c.total_amount, Ortalama: c.avg_amount })),
      ];
    },
    driver: () => {
      const { driverStats, deliveryTimeStats } = rawData;
      return [
        ...driverStats.filter((d: any) => d.total_shipments > 0).map((d: any) => ({
          Sürücü: d.full_name,
          Sehir: d.city || '-',
          Teslimat: d.delivered,
          Iptal: d.cancelled,
          'Toplam KM': Math.round(d.total_km),
          Gelir: d.total_revenue,
          'TL/KM': Math.round(d.avg_tl_km),
          Puan: d.rating?.toFixed(1),
        })),
        ...deliveryTimeStats.map((dt: any) => ({
          Sürücü: dt.full_name,
          'Ort. Süre (saat)': dt.avg_delivery_hours?.toFixed(1),
          'Min Süre': dt.min_delivery_hours?.toFixed(1),
          'Max Süre': dt.max_delivery_hours?.toFixed(1),
        })),
      ];
    },
    harcirah: () => {
      const { rows = [], summary = {} } = rawData || {};
      return [
        { Metrik: 'Özmal Sürücü Sayısı', Deger: Number(summary.driverCount || 0) },
        { Metrik: 'Toplam Sevkiyat', Deger: Number(summary.totalShipmentCount || 0) },
        { Metrik: 'Toplam Aylık Sabit Maaş', Deger: Number(summary.totalAylikSabitMaas || 0) },
        { Metrik: 'Toplam Seyir KM', Deger: Math.round(Number(summary.totalKm || 0)) },
        { Metrik: 'Toplam KM Primi', Deger: Number(summary.totalKmPrimi || 0) },
        { Metrik: 'Toplam Konaklama Gece', Deger: Number(summary.totalWaitingNightCount || 0) },
        { Metrik: 'Toplam Bekleme Bedeli', Deger: Number(summary.totalWaitingFee || 0) },
        { Metrik: 'Toplam 200km Altı Sefer', Deger: Number(summary.totalShortDistanceCount || 0) },
        { Metrik: 'Toplam 200km Altı Prim', Deger: Number(summary.totalShortDistancePrim || 0) },
        { Metrik: 'Toplam Yük Ortağım KM', Deger: Math.round(Number(summary.totalYukOrtagimKm || 0)) },
        { Metrik: 'Toplam Workiom KM', Deger: Math.round(Number(summary.totalWorkiomKm || 0)) },
        ...rows.map((row: any) => ({
          Sürücü: row.driverName,
          'Harcırah Türü': row.harcirahTuru || '-',
          'Aylık Sabit Maaş': Number(row.aylikSabitMaas || 0),
          İkamet: row.residenceCity || '-',
          Çekici: row.plate || '-',
          Sevkiyat: row.shipmentCount || 0,
          'Seyir KM': Math.round(Number(row.seferkm || 0)),
          'KM Primi': Number(row.kmPrimi || 0),
          'Yük Ortağım KM': Math.round(Number(row.yukOrtagimKm || 0)),
          'Workiom KM': Math.round(Number(row.workiomKm || 0)),
          'Ara Sefer Sayısı': Number(row.transferCount || 0),
          'Konaklama Gece': Number(row.waitingNightCount || 0),
          'Bekleme Bedeli': Number(row.waitingFee || 0),
          '200km Altı Sefer': Number(row.shortDistanceCount || 0),
          '200km Altı Prim': Number(row.shortDistancePrim || 0),
          'Hammaliye': Number(row.hamaliyeFee || 0),
          'Toplam Harcırah': Number(row.toplamHarcirah || 0),
          'Kesintiler(-)': Number(row.kesintiler || 0),
          'AGİ(+)': Number(row.agi || 0),
        })),
      ];
    },
    vehicle: () => {
      const { vehicleStats } = rawData;
      return vehicleStats.map((v: any) => ({
        Plaka: v.plate,
        Marka: `${v.brand} ${v.model}`,
        Tip: v.vehicle_type,
        Durum: v.status,
        'Günlük KM': v.daily_km || '-',
        Yakıt: v.fuel_level ? `${v.fuel_level}%` : '-',
        Sefer: v.total_shipments,
        'Toplam KM': Math.round(v.total_km),
        Gelir: v.total_revenue,
      }));
    },
    customer: () => {
      const { topCustomers, cityDistribution } = rawData;
      return [
        ...topCustomers.map((c: any, i: number) => ({
          Sira: i + 1,
          Müşteri: c.company_name,
          Sehir: `${c.city} / ${c.district}`,
          Tip: c.customer_type,
          Sefer: c.shipment_count,
          Gelir: c.total_revenue,
          'Ort. Değer': Math.round(c.avg_shipment_value),
        })),
        ...cityDistribution.map((cd: any) => ({ Sehir: cd.city, MüşteriSayisi: cd.customer_count })),
      ];
    },
    route: () => {
      const { topRoutes, distanceDistribution } = rawData;
      return [
        ...topRoutes.map((r: any) => ({
          Rota: r.route,
          Sefer: r.shipment_count,
          'Ort. Mesafe (km)': Math.round(r.avg_distance),
          'Ort. Fiyat': Math.round(r.avg_price),
          'Toplam Gelir': r.total_revenue,
          'TL/KM': Math.round(r.avg_tl_km),
        })),
        ...distanceDistribution.map((d: any) => ({
          MesafeAraligi: d.distance_range,
          SeferSayisi: d.count,
          'Ort. Fiyat': Math.round(d.avg_price),
        })),
      ];
    },
    bid: () => {
      const { bidStats, driverBidStats } = rawData;
      return [
        { Metrik: 'Toplam Teklif', Deger: bidStats.total_bids },
        { Metrik: 'Bekleyen', Deger: bidStats.pending },
        { Metrik: 'Kabul Edilen', Deger: bidStats.accepted },
        { Metrik: 'Reddedilen', Deger: bidStats.rejected },
        { Metrik: 'Kabul Oranı (%)', Deger: bidStats.total_bids > 0 ? ((bidStats.accepted / bidStats.total_bids) * 100).toFixed(1) : '0' },
        ...driverBidStats.map((d: any) => ({
          Sürücü: d.driver_name,
          ToplamTeklif: d.total_bids,
          Kabul: d.accepted,
          'Ort. Teklif': Math.round(d.avg_bid),
        })),
      ];
    },
    ontime: () => {
      const { onTimeStats, lateDeliveries } = rawData;
      const onTimeRate = onTimeStats.total_delivered > 0 ? ((onTimeStats.on_time / onTimeStats.total_delivered) * 100).toFixed(1) : '0';
      return [
        { Metrik: 'Teslim Edilen', Deger: onTimeStats.total_delivered },
        { Metrik: 'Zamanında', Deger: onTimeStats.on_time },
        { Metrik: 'Geciken', Deger: onTimeStats.late },
        { Metrik: 'Süresiz', Deger: onTimeStats.no_deadline },
        { Metrik: 'Zamanında Teslimat Oranı (%)', Deger: onTimeRate },
        { Metrik: 'Ort. Teslimat Süresi (saat)', Deger: onTimeStats.avg_delivery_hours?.toFixed(1) },
        ...lateDeliveries.map((d: any) => ({
          SevkKodu: d.shipment_no,
          Rota: `${d.pickup_city} → ${d.delivery_city}`,
          Sürücü: d.driver_name,
          Plaka: d.cekici_plaka,
          Beklenen: d.expected_date,
          Gerçeklesen: d.actual_date,
          'Gecikme (saat)': Math.round(d.late_hours),
        })),
      ];
    },
    accounting: () => {
      const { accountingSummary, pendingInvoices } = rawData;
      return [
        { Metrik: 'Fatura Yok', Deger: accountingSummary.no_invoice },
        { Metrik: 'Fatura Yüklendi', Deger: accountingSummary.invoice_uploaded },
        { Metrik: 'Onaylandı', Deger: accountingSummary.invoice_approved },
        { Metrik: 'Ödeme Bekliyor', Deger: accountingSummary.payment_pending },
        { Metrik: 'Ödeme Tamamlandı', Deger: accountingSummary.payment_completed },
        { Metrik: 'Toplam Fatura Tutarı', Deger: accountingSummary.total_invoice_amount },
        { Metrik: 'Toplam Ödenen', Deger: accountingSummary.total_paid },
        ...pendingInvoices.map((s: any) => ({
          SevkKodu: s.shipment_no,
          Rota: `${s.pickup_city} → ${s.delivery_city}`,
          Sürücü: s.driver_name,
          Navlun: s.final_price,
          'Fatura Tutarı': s.invoice_amount || '-',
          'Fatura Kodu': s.invoice_code || '-',
          Durum: s.accounting_status || 'none',
        })),
      ];
    },
    waiting: () => {
      const { waitingSummary, waitingByLocation, waitingByDriver } = rawData;
      return [
        { Metrik: 'Toplam Bekleme Kaydı', Deger: waitingSummary.total_waiting_records },
        { Metrik: 'Toplam Süre (dk)', Deger: Math.round(waitingSummary.total_waiting_minutes) },
        { Metrik: 'Ort. Süre (dk)', Deger: Math.round(waitingSummary.avg_waiting_minutes) },
        { Metrik: 'Max Süre (dk)', Deger: Math.round(waitingSummary.max_waiting_minutes) },
        { Metrik: 'Onaylanan', Deger: waitingSummary.approved },
        { Metrik: 'Bekleyen', Deger: waitingSummary.pending },
        { Metrik: 'Reddedilen', Deger: waitingSummary.rejected },
        ...waitingByLocation.map((w: any) => ({
          Konum: w.location,
          BeklemeSayisi: w.waiting_count,
          'Ort. Süre (dk)': Math.round(w.avg_waiting_minutes),
          'Toplam Süre (dk)': Math.round(w.total_waiting_minutes),
        })),
        ...waitingByDriver.map((w: any) => ({
          Sürücü: w.driver_name,
          Plaka: w.cekici_plaka,
          BeklemeSayisi: w.waiting_count,
          'Ort. Süre (dk)': Math.round(w.avg_waiting_minutes),
        })),
      ];
    },
  };

  return (exporters[reportType] || (() => []))();
}
